import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { api, STATIC_BASE } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import ProductCard from "@/components/ProductCard";
import { ArrowRight, Sparkles, Gift, Flame } from "lucide-react";

/**
 * Occasion definitions — hero styling + product filter.
 * Products are filtered on the client from the full catalogue by slug or category.
 * Curated lists ensure the right story on each page.
 */
const OCCASIONS = {
    diwali: {
        label: "Diwali",
        eyebrow: "— The Festival of Lights",
        title: "For the golden evenings.",
        titleItalic: "Diwali, at your table.",
        subtitle: "Zardosi wall panels, gold-embroidered cushions and heirloom kundan — the pieces that turn a lamp-lit night into a memory.",
        heroImg: `${STATIC_BASE}/uploads/prod-27.jpeg`,
        heroCap: "Signature · Golden Bouquet Zardosi Panel",
        theme: {
            accent: "#C5A059",
            bg: "from-[#2a1a0d] via-ink-900 to-ink-900",
            glow: "radial-gradient(circle, #C5A059 0%, transparent 60%)",
        },
        // Products to feature for Diwali
        slugs: [
            "golden-bouquet-zardosi-wall-panel",
            "crimson-zardosi-cushion-set",
            "heraldic-lion-blue-cushion",
            "songbirds-velvet-panel",
            "gemtone-oval-clutches",
            "zardosi-rose-heart-ornament",
            "aqua-zardosi-heart-ornament",
            "amethyst-beaded-star",
            "kundan-choker-set",
            "polki-jhumka-earrings",
            "meenakari-bangles-set",
            "temple-jhumki-long",
            "royal-garam-masala",
            "biryani-masala",
        ],
        collections: [
            { title: "For the drawing room", note: "Zardosi and velvet, catching lamp-light.", categories: ["wall-hangings", "cushion-covers"] },
            { title: "For the ear and neck", note: "Kundan, polki, meenakari — heirloom-quality artisan jewelry.", categories: ["necklaces", "earrings", "bangles"] },
            { title: "For the mithai table", note: "Sweeter spice blends and biryani-worthy masalas.", categories: ["blended-spices", "whole-spices"] },
        ],
    },
    christmas: {
        label: "Christmas",
        eyebrow: "— A Handmade Christmas",
        title: "Beneath the tree.",
        titleItalic: "On the mantle.",
        subtitle: "Zari-thread baubles, silk-velvet stockings, tree skirts and hand-beaded ornaments — every piece worked by hand, none of it mass-made.",
        heroImg: `${STATIC_BASE}/uploads/prod-15.jpeg`,
        heroCap: "Signature · Crimson Velvet Tree Skirt",
        theme: {
            accent: "#C5A059",
            bg: "from-[#1a0f0f] via-ink-900 to-ink-900",
            glow: "radial-gradient(circle, #C5A059 0%, transparent 60%)",
        },
        slugs: [
            "crimson-velvet-tree-skirt",
            "snowflake-velvet-stocking",
            "zari-twine-baubles-classic",
            "rainbow-thread-baubles",
            "ombre-thread-baubles",
            "zardosi-rose-heart-ornament",
            "aqua-zardosi-heart-ornament",
            "amethyst-beaded-star",
            "twisted-brass-napkin-rings-set",
            "silver-brass-coiled-napkin-rings",
            "bronze-angel-napkin-ring",
            "golden-butterfly-napkin-ring",
            "chai-masala",
            "cardamom-green-pods",
        ],
        collections: [
            { title: "On the tree", note: "Ornaments, tree skirts and hand-couched stockings.", categories: ["tree-skirts", "ornaments"] },
            { title: "At the table", note: "Sculptural brass napkin rings for Christmas dinner.", categories: ["napkin-rings"] },
            { title: "In the mug", note: "Chai, cardamom, warmth — for the cold nights.", categories: ["blended-spices", "whole-spices"] },
        ],
    },
};

export default function FestivePage() {
    const { occasion } = useParams();
    const config = OCCASIONS[occasion];
    const { t } = useI18n();
    const [products, setProducts] = useState([]);

    useEffect(() => {
        window.scrollTo(0, 0);
        api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
    }, []);

    // Filter products by curated slugs (falls back to categories if slug missing)
    const filtered = useMemo(() => {
        if (!config) return [];
        const bySlug = new Map(products.map((p) => [p.slug, p]));
        return config.slugs.map((s) => bySlug.get(s)).filter(Boolean);
    }, [products, config]);

    if (!config) return <Navigate to="/shop" replace />;

    return (
        <div data-testid={`festive-${occasion}`} className={`bg-gradient-to-b ${config.theme.bg}`}>
            {/* HERO */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0">
                    <img src={config.heroImg} alt="" className="w-full h-full object-cover opacity-25" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/60 to-ink-900/40" />
                </div>
                <div
                    className="pointer-events-none absolute -top-1/3 right-0 w-[700px] h-[700px] rounded-full opacity-[0.25] blur-[120px]"
                    style={{ background: config.theme.glow }}
                />
                <div className="relative max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10 pt-20 sm:pt-28 lg:pt-36 pb-16 sm:pb-24">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-7 order-2 lg:order-1">
                            <div className="flex items-center gap-3 mb-6 sm:mb-8">
                                <span className="w-10 h-10 flex items-center justify-center border border-brass-400/50 text-brass-400">
                                    {occasion === "diwali" ? <Flame className="w-4 h-4" strokeWidth={1.5} /> : <Gift className="w-4 h-4" strokeWidth={1.5} />}
                                </span>
                                <p className="text-[10px] sm:text-[11px] tracking-[0.32em] sm:tracking-[0.4em] uppercase text-brass-400">
                                    {config.eyebrow}
                                </p>
                            </div>
                            <h1 className="font-serif text-[44px] sm:text-[64px] lg:text-[84px] leading-[0.94] tracking-tight text-bone-100 mb-6 sm:mb-8 animate-fade-up">
                                {config.title}
                                <br />
                                <span className="italic text-brass-400">{config.titleItalic}</span>
                            </h1>
                            <p className="text-base sm:text-lg text-bone-100/85 max-w-xl font-light leading-relaxed mb-10 animate-fade-up animate-fade-up-delay-1">
                                {config.subtitle}
                            </p>
                            <div className="flex flex-wrap items-center gap-4 sm:gap-6 animate-fade-up animate-fade-up-delay-2">
                                <a
                                    href="#festive-grid"
                                    className="group inline-flex items-center gap-3 sm:gap-4 border border-brass-400 text-brass-400 hover:bg-brass-400 hover:text-ink-900 transition-colors duration-500 px-6 sm:px-8 py-3.5 sm:py-4 text-[10px] sm:text-[11px] tracking-[0.24em] sm:tracking-[0.28em] uppercase"
                                >
                                    Shop the {config.label} edit
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </a>
                                <span className="text-[10px] sm:text-[11px] tracking-[0.24em] uppercase text-bone-300/70">
                                    {filtered.length} pieces · gift-ready
                                </span>
                            </div>
                        </div>
                        <div className="lg:col-span-5 order-1 lg:order-2 relative">
                            <div className="relative aspect-[3/4] max-w-[480px] mx-auto lg:ml-auto lg:mr-0 overflow-hidden card-image-wrap border border-brass-400/25 shadow-[0_30px_80px_-20px_rgba(197,160,89,0.45)]">
                                <img src={config.heroImg} alt={config.heroCap} className="w-full h-full object-cover animate-ken-burns" />
                                <div className="pointer-events-none absolute inset-0">
                                    <div className="absolute top-0 left-0 w-8 h-8 border-t border-l border-brass-400" />
                                    <div className="absolute top-0 right-0 w-8 h-8 border-t border-r border-brass-400" />
                                    <div className="absolute bottom-0 left-0 w-8 h-8 border-b border-l border-brass-400" />
                                    <div className="absolute bottom-0 right-0 w-8 h-8 border-b border-r border-brass-400" />
                                </div>
                            </div>
                            <div className="hidden sm:block absolute -bottom-4 -left-4 lg:-left-6 bg-ink-800/95 backdrop-blur border border-brass-400/30 px-4 py-2.5 max-w-[260px]">
                                <p className="text-[9px] tracking-[0.28em] uppercase text-brass-400">{config.heroCap}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Collections mini-rail */}
            <section className="py-16 sm:py-20 border-t border-white/5">
                <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10">
                    <p className="text-[10px] sm:text-[11px] tracking-[0.32em] uppercase text-brass-400 mb-3">— The edit</p>
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-bone-100 mb-10 sm:mb-14 tracking-tight">
                        Three rooms, three chapters.
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {config.collections.map((c, i) => (
                            <div key={i} className="border border-white/8 bg-ink-800/40 p-6 sm:p-7 hover:border-brass-400/40 transition-colors group">
                                <div className="flex items-center justify-between mb-4">
                                    <span className="font-serif italic text-3xl text-brass-400 tabular-nums">0{i + 1}</span>
                                    <Sparkles className="w-4 h-4 text-brass-400/70 group-hover:text-brass-400 transition-colors" strokeWidth={1.5} />
                                </div>
                                <h3 className="font-serif text-2xl text-bone-100 leading-tight mb-2">{c.title}</h3>
                                <p className="text-sm text-bone-300 font-light leading-relaxed">{c.note}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Product grid */}
            <section id="festive-grid" className="py-16 sm:py-20 lg:py-28 border-t border-white/5">
                <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 sm:mb-14 gap-4">
                        <div>
                            <p className="text-[10px] sm:text-[11px] tracking-[0.32em] uppercase text-brass-400 mb-3">— The {config.label} edit</p>
                            <h2 className="font-serif text-4xl sm:text-5xl text-bone-100 tracking-tight leading-none">
                                Curated for the occasion.
                            </h2>
                        </div>
                        <Link
                            to={occasion === "diwali" ? "/shop/jewelry" : "/shop/home-decor"}
                            className="text-[10px] sm:text-[11px] tracking-[0.28em] uppercase text-brass-400 link-hairline self-start md:self-auto"
                        >
                            See the full house →
                        </Link>
                    </div>
                    {filtered.length === 0 ? (
                        <div className="text-center py-20 text-bone-300 text-sm tracking-[0.2em] uppercase">Loading…</div>
                    ) : (
                        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 sm:gap-x-6 gap-y-10 sm:gap-y-14">
                            {filtered.map((p) => <ProductCard key={p.slug} product={p} />)}
                        </div>
                    )}
                </div>
            </section>

            {/* CTA banner */}
            <section className="py-16 sm:py-20 border-t border-white/5">
                <div className="max-w-[900px] mx-auto px-5 sm:px-6 text-center">
                    <p className="text-[10px] sm:text-[11px] tracking-[0.32em] uppercase text-brass-400 mb-4">— Gifting</p>
                    <h2 className="font-serif italic text-3xl sm:text-4xl lg:text-5xl text-bone-100 leading-tight mb-6">
                        A gift they'll unpack — and then repack, into a lifetime.
                    </h2>
                    <p className="text-base sm:text-lg text-bone-300 font-light leading-relaxed mb-8">
                        Every {config.label} order arrives gift-wrapped in kraft, tied with jute, and slipped with a hand-written note from the maker. Complimentary within Europe on orders over €120.
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
                        <a href="#festive-grid" className="border border-brass-400 text-brass-400 hover:bg-brass-400 hover:text-ink-900 transition-colors duration-500 px-6 sm:px-8 py-3.5 sm:py-4 text-[10px] sm:text-[11px] tracking-[0.24em] sm:tracking-[0.28em] uppercase">
                            Choose your gift
                        </a>
                        <Link to="/wholesale" className="text-[10px] sm:text-[11px] tracking-[0.24em] uppercase text-bone-300 hover:text-brass-400 transition-colors link-hairline">
                            Corporate gifting →
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
